import React, { useMemo } from 'react'
import { Box, Modal } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { SidebarNavigationItem } from '../../../../shared/types/navigation'
import { PlatformTourPhase } from '../../../../store/slices/uiSlice'
import { GamerRoutes } from '../../../../config/routes/routeEnums'
import { useUser } from '../../../../hooks/useUser'
import { hasIncompleteOnboardingSteps } from '../../pages/onboardingSteps.config'
import { buildPlatformTourStops, getTourGreetingName, isPlatformTourOnScreen } from '../platformTourStops'
import { usePlatformTourControls } from '../hooks/usePlatformTourControls'
import { usePlatformTourNavigation } from '../hooks/usePlatformTourNavigation'
import { useTourPanelPlacement } from '../hooks/useTourPanelPlacement'
import { PlatformTourBackdrop } from './PlatformTourBackdrop'
import { PlatformTourWelcome } from './PlatformTourWelcome'
import { PlatformTourStopPanel } from './PlatformTourStopPanel'
import { PlatformTourDone } from './PlatformTourDone'
import { TOUR_Z_INDEX, tourScreenSx } from './platformTourStyles'

/** The platform tour over the gamer shell: welcome, one stop per menu page, and the closing card. */
export const PlatformTour: React.FC<{ navigationItems: SidebarNavigationItem[] }> = ({ navigationItems }) => {
  const navigate = useNavigate()
  const { data: user } = useUser()
  const controls = usePlatformTourControls()
  const { tour } = controls
  const stops = useMemo(() => buildPlatformTourStops(navigationItems), [navigationItems])
  const stopIndex = Math.min(tour?.stopIndex ?? 0, stops.length - 1)
  const isOnStop = tour?.phase === PlatformTourPhase.STOP && stops.length > 0
  const currentSection = isOnStop ? stops[stopIndex].section : undefined
  const placement = useTourPanelPlacement(currentSection)

  usePlatformTourNavigation(currentSection)

  const isOnScreen = isPlatformTourOnScreen(tour)
  const isLastStop = stopIndex === stops.length - 1
  const isOnboardingFinished = !hasIncompleteOnboardingSteps(user)

  const handleNext = () => (isLastStop ? controls.finish() : controls.showStop(stopIndex + 1))

  // Onboarding still outstanding comes before the dashboard.
  const handleContinue = () => {
    controls.end()
    navigate(isOnboardingFinished ? GamerRoutes.DASHBOARD : GamerRoutes.ONBOARDING)
  }

  // Escape leaves the way the close control on screen does.
  const handleEscape = () => {
    if (tour?.phase === PlatformTourPhase.WELCOME) controls.close()
    if (tour?.phase === PlatformTourPhase.STOP) controls.pause()
    if (tour?.phase === PlatformTourPhase.DONE) controls.end()
  }

  return (
    <Modal open={isOnScreen} onClose={handleEscape} hideBackdrop disableScrollLock sx={{ zIndex: TOUR_Z_INDEX }}>
      <Box sx={tourScreenSx} tabIndex={-1}>
        <PlatformTourBackdrop spotlightRect={isOnStop ? placement.anchorRect : null} />

        {tour?.phase === PlatformTourPhase.WELCOME && (
          <PlatformTourWelcome
            name={getTourGreetingName(user)}
            stopCount={stops.length}
            onStart={() => controls.showStop(0)}
            onLater={controls.close}
          />
        )}

        {isOnStop && (
          <PlatformTourStopPanel
            stops={stops}
            stopIndex={stopIndex}
            placement={placement}
            onBack={() => controls.showStop(stopIndex - 1)}
            onNext={handleNext}
            onSkip={controls.skip}
            onPause={controls.pause}
          />
        )}

        {tour?.phase === PlatformTourPhase.DONE && (
          <PlatformTourDone
            isOnboardingFinished={isOnboardingFinished}
            onContinue={handleContinue}
            onReplay={() => controls.showStop(0)}
          />
        )}
      </Box>
    </Modal>
  )
}
